import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, CalendarCheck, CalendarDays, Clock3 } from "lucide-react";
import Navbar from "../components/marketing/Navbar";
import Footer from "../components/marketing/Footer";
import { Container, Reveal } from "../components/marketing/common";
import { useDemoModal } from "../components/marketing/DemoModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Block = ({ block }) => {
    if (block.type === "h2")
        return <h2 className="mt-10 font-display text-2xl font-bold tracking-tight text-[#111827]">{block.text}</h2>;
    if (block.type === "list")
        return (
            <ul className="mt-4 grid gap-3">
                {block.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-[16px] leading-relaxed text-[#475569]">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#16A34A]" />
                        {item}
                    </li>
                ))}
            </ul>
        );
    return <p className="mt-4 text-[16px] leading-[1.8] text-[#475569]">{block.text}</p>;
};

export default function BlogPost() {
    const { slug } = useParams();
    const { openDemo } = useDemoModal();
    const [post, setPost] = useState(null);
    const [state, setState] = useState("loading");

    useEffect(() => {
        setState("loading");
        axios.get(`${API}/blog/posts/${slug}`)
            .then((r) => {
                setPost(r.data);
                setState("ok");
                document.title = `${r.data.title} | Kryvo CRM Blog`;
            })
            .catch(() => setState("missing"));
    }, [slug]);

    return (
        <div className="min-h-screen bg-white">
            <Navbar />
            <main className="pt-[68px]">
                {state === "loading" && (
                    <p className="py-32 text-center text-sm text-[#94A3B8]">Loading article…</p>
                )}
                {state === "missing" && (
                    <div className="py-32 text-center">
                        <p className="font-display text-2xl font-bold text-[#111827]">Article not found</p>
                        <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#16A34A]">
                            <ArrowLeft className="h-4 w-4" /> Back to the blog
                        </Link>
                    </div>
                )}
                {state === "ok" && post && (
                    <article className="py-14 lg:py-20">
                        <Container>
                            <div className="mx-auto max-w-3xl">
                                <Reveal>
                                    <Link
                                        to="/blog"
                                        data-testid="blogpost-back-link"
                                        className="inline-flex items-center gap-2 text-sm font-semibold text-[#64748B] transition-colors hover:text-[#15803D]"
                                    >
                                        <ArrowLeft className="h-4 w-4" /> All articles
                                    </Link>
                                    <div className="mt-7 flex flex-wrap items-center gap-3">
                                        <span className="rounded-full bg-[#F0FDF4] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#15803D]">
                                            {post.category}
                                        </span>
                                        <span className="flex items-center gap-1.5 text-xs font-medium text-[#94A3B8]">
                                            <CalendarDays className="h-3 w-3" />
                                            {new Date(post.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                                        </span>
                                        <span className="flex items-center gap-1.5 text-xs font-medium text-[#94A3B8]">
                                            <Clock3 className="h-3 w-3" /> {post.read_minutes} min read
                                        </span>
                                    </div>
                                    <h1 className="mt-5 text-balance font-display text-3xl font-extrabold leading-tight tracking-tight text-[#111827] sm:text-4xl">
                                        {post.title}
                                    </h1>
                                    <p className="mt-4 border-l-2 border-[#16A34A] pl-4 text-base leading-relaxed text-[#64748B]">
                                        {post.excerpt}
                                    </p>
                                </Reveal>
                                <Reveal delay={0.1}>
                                    <div className="mt-4">
                                        {post.content.map((b, i) => <Block key={i} block={b} />)}
                                    </div>
                                </Reveal>

                                {/* CTA */}
                                <Reveal delay={0.05}>
                                    <div className="mt-14 rounded-3xl bg-gradient-to-br from-[#15803D] via-[#16A34A] to-[#166534] p-8 text-center sm:p-10">
                                        <h3 className="font-display text-2xl font-extrabold text-white">
                                            See this working inside Kryvo CRM
                                        </h3>
                                        <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-white/85">
                                            Book a 30-minute demo and we'll walk through these exact workflows with your own sales process.
                                        </p>
                                        <div className="mt-7 flex flex-wrap justify-center gap-3">
                                            <button
                                                onClick={openDemo}
                                                data-testid="blogpost-btn-book-demo"
                                                className="flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#15803D] transition-all hover:-translate-y-0.5 hover:shadow-xl"
                                            >
                                                <CalendarCheck className="h-4 w-4" /> Book a Demo
                                            </button>
                                            <Link
                                                to="/signup"
                                                data-testid="blogpost-btn-start-free"
                                                className="group flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-white/10"
                                            >
                                                Start Free
                                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                            </Link>
                                        </div>
                                    </div>
                                </Reveal>
                            </div>
                        </Container>
                    </article>
                )}
            </main>
            <Footer />
        </div>
    );
}
